import {
  entityRefToUrn,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import {
  EnabledTeamRoleInfo,
  ResolveTeamRoleParams,
  TeamRoleEnum,
} from '@agilelab/plugin-wb-team-roles-common';
import { stringifyEntityRef } from '@mainstage/catalog-model';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import { useTeamRoleAssignments } from '../hooks';
import { TeamRoleAssignment, TeamRoleAssignmentKindEnum } from '../types';

type TeamRolesPanelContextProps = {
  project: WitboostSystem;
  projectUrn: string;
  selectedTeamRole: TeamRoleEnum;
  setSelectedTeamRole: Dispatch<SetStateAction<TeamRoleEnum>>;
  openCreateTeamRoleAssignmentDrawer: boolean;
  setOpenCreateTeamRoleAssignmentDrawer: Dispatch<SetStateAction<boolean>>;
  isFeatureEnabled: boolean;
  hasTroubleshootAccess: boolean;
  isOwner: boolean;
  isLimitedOwner: boolean;
  hasPrincipalOwner: boolean;
  hasMultiplePrincipalOwners: boolean;
  isCatalogInfoOwner: boolean;
  enabledTeamRoles: EnabledTeamRoleInfo[];
  assignments: TeamRoleAssignment[] | undefined;
  error: Error | undefined;
  isLoading: boolean;
  canHaveLimitedAssignees: boolean;
  refresh: () => void;
  revokeAccess: () => void;
};

export const TeamRolesPanelContext = createContext<TeamRolesPanelContextProps>(
  {} as TeamRolesPanelContextProps,
);

interface TeamRolesPanelContextProviderProps {
  children?: ReactNode;
  project: WitboostSystem;
  isFeatureEnabled: boolean;
  hasTroubleshootAccess: boolean;
  isOwner: boolean;
  isLimitedOwner: boolean;
  hasPrincipalOwner: boolean;
  isCatalogInfoOwner: boolean;
  enabledTeamRoles: EnabledTeamRoleInfo[];
  refresh: () => void;
  revokeAccess: () => void;
}

export const TeamRolesPanelContextProvider: FC<
  TeamRolesPanelContextProviderProps
> = ({ children, ...props }) => {
  const [selectedTeamRole, setSelectedTeamRole] = useState<TeamRoleEnum>(
    TeamRoleEnum.OWNER,
  );
  const [
    openCreateTeamRoleAssignmentDrawer,
    setOpenCreateTeamRoleAssignmentDrawer,
  ] = useState(false);

  const stringifiedEntityRef = stringifyEntityRef({
    kind: props.project?.kind ?? 'system',
    namespace: 'default',
    name: props.project?.metadata.name ?? '',
  });
  const projectUrn = entityRefToUrn(stringifiedEntityRef);

  const params: ResolveTeamRoleParams = {
    teamRole: selectedTeamRole,
    projectUrn,
    mode: 'exact-match',
    useFallback: false,
  };

  const { data, error, isLoading } = useTeamRoleAssignments(params);

  const hasMultiplePrincipalOwners = useMemo(() => {
    if (!data?.assignments) return false;
    return (
      data.assignments.filter(a =>
        a.kinds.includes(TeamRoleAssignmentKindEnum.FULL),
      ).length > 1
    );
  }, [data]);

  const canHaveLimitedAssignees = useMemo(
    () =>
      props.enabledTeamRoles.find(
        teamRole => teamRole.name === selectedTeamRole,
      )?.canHaveLimitedAssignees,
    [props.enabledTeamRoles, selectedTeamRole],
  );

  return (
    <TeamRolesPanelContext.Provider
      value={{
        ...props,
        projectUrn,
        selectedTeamRole,
        setSelectedTeamRole,
        openCreateTeamRoleAssignmentDrawer,
        setOpenCreateTeamRoleAssignmentDrawer,
        assignments: data?.assignments ?? [],
        error: error ?? undefined,
        isLoading,
        hasMultiplePrincipalOwners,
        canHaveLimitedAssignees: canHaveLimitedAssignees ?? false,
      }}
    >
      {children}
    </TeamRolesPanelContext.Provider>
  );
};
